import { ValueFormatterParams } from "ag-grid-community";
import { ArrowDown, ArrowUp, CalendarDays, ChevronDown, Pencil, Plus, Wallet } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import CustomModal from "../../../../components/CustomModal";
import Grid from "../../../../components/Grid";
import CustomStatusFilter from "../../../../components/Grid/components/CustomStatusFilter";
import { ColumnDef, GridHandle } from "../../../../components/Grid/model/Grid";
import ModalDelete from "../../../../components/ModalDelete";
import Loading from "../../../../core/common/Loading";
import { useAuth } from "../../../../hooks/AuthenticateContext";
import { useModal } from "../../../../hooks/ModalContext";
import { usePermissions } from "../../../../hooks/PermissionContext";
import api from "../../../../services/api";
import { FrontendNotification } from "../../../../shared/Notification";
import Create, { type CaixaMovementType } from "./Create";
import Info from "./Info";
import { resolveTipoMovimentacao, TIPO_BADGE_CLASS } from "./tipoMovimentacao";
import { ICaixaGeral, ICaixaGeralResumo } from "./types/types";

type DashboardPeriod = "month" | "quarter" | "semester" | "year";

// Opções do filtro (modo seleção) da coluna "Tipo". O `id` é o valor enviado
// ao backend em `tipo_movimentacao`; o `icon` é a bolinha de cor no dropdown.
const TIPO_MOVIMENTACAO_FILTER_OPTIONS = [
  { id: "SANGRIA", label: "Sangria", icon: "#10B981" },
  { id: "FECHAMENTO_CAIXA", label: "Fechamento de caixa", icon: "#10B981" },
  { id: "ABERTURA_CAIXA", label: "Abertura de caixa", icon: "#EF4444" },
  { id: "DEPOSITO_MANUAL", label: "Depósito", icon: "#10B981" },
  { id: "RETIRADA_MANUAL", label: "Retirada", icon: "#EF4444" },
  { id: "EDICAO_COFRE", label: "Edição valor no cofre", icon: "#9CA3AF" },
];

const DASHBOARD_PERIODS: Array<{
  value: DashboardPeriod;
  label: string;
  shortLabel: string;
  days: number;
  points: number;
}> = [
  { value: "month", label: "Último mês", shortLabel: "Mês", days: 30, points: 5 },
  { value: "quarter", label: "Último trimestre", shortLabel: "Trimestre", days: 90, points: 6 },
  { value: "semester", label: "Último semestre", shortLabel: "Semestre", days: 180, points: 6 },
  { value: "year", label: "Último ano", shortLabel: "Ano", days: 365, points: 12 },
];

const AnimatedNumber: React.FC<{
  value: number;
  formatter?: (value: number) => string;
  className?: string;
}> = ({ value, formatter = (numberValue) => String(Math.round(numberValue)), className }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const previousValueRef = useRef(value);

  useEffect(() => {
    const startValue = previousValueRef.current;
    const change = value - startValue;
    const duration = 650;
    const startTime = performance.now();
    let animationFrame = 0;

    const animate = (currentTime: number) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(startValue + change * easedProgress);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        previousValueRef.current = value;
        setDisplayValue(value);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [value]);

  return <span className={className}>{formatter(displayValue)}</span>;
};

const CaixaGeral: React.FC = () => {
  const [selectedRow, setSelectedRow] = useState<ICaixaGeral | undefined>();
  const [isEdit, setIsEdit] = useState(false);
  const [isRemove, setIsRemove] = useState<boolean>(false);
  const [isView, setIsView] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [dashboardLoading, setDashboardLoading] = useState<boolean>(false);
  const [chartProgress, setChartProgress] = useState(0);
  const [dashboardRows, setDashboardRows] = useState<ICaixaGeral[]>([]);
  const [resumo, setResumo] = useState<ICaixaGeralResumo | null>(null);
  const [dashboardPeriod, setDashboardPeriod] = useState<DashboardPeriod>("month");
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [selectedMovementType, setSelectedMovementType] = useState<CaixaMovementType>("deposito");
  const [isCorrectionConfirmOpen, setIsCorrectionConfirmOpen] = useState(false);
  const [isCorrectionModalOpen, setIsCorrectionModalOpen] = useState(false);
  const [correctedVaultValue, setCorrectedVaultValue] = useState<number | undefined>();
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const gridRef = useRef<GridHandle>(null);
  const addMenuRef = useRef<HTMLDivElement>(null);
  const { openModal, isModalOpen, closeModal } = useModal();
  const { user } = useAuth();

  const canDeposit = usePermissions("DEPOSITAR");
  const canWithdraw = usePermissions("RETIRAR");
  const canEditCofre = usePermissions("EDITARCOFRE");
  const canUseAddMenu = canDeposit || canWithdraw;

  const selectedDashboardPeriod = DASHBOARD_PERIODS.find((period) => period.value === dashboardPeriod) || DASHBOARD_PERIODS[0];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatCurrencyCompact = (value: number) => (
    formatCurrency(value).replace("R$", "R$ ")
  );

  const getMovementDescription = (row?: ICaixaGeral) => {
    if (row?.justificativa) return row.justificativa;
    return `${resolveTipoMovimentacao(row).label} registrada no caixa geral`;
  };

  const formatDateToApi = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const columns: ColumnDef[] = [
    {
      field: "data_historico",
      headerName: "Data / Hora",
      filter: true,
      filterParams: {
        dateBetween: true,
      },
      width: 190,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return "-";
        return new Intl.DateTimeFormat("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(String(params.value).replace(" ", "T")));
      },
    },
    {
      field: "usuario",
      fieldName: "usuario",
      headerName: "Operador",
      filter: true,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          return params.value;
        }
        return "---";
      },
    },
    {
      field: "tipo_movimentacao",
      headerName: "Tipo",
      filter: CustomStatusFilter,
      filterParams: { status: TIPO_MOVIMENTACAO_FILTER_OPTIONS },
      sortable: false,
      width: 190,
      valueGetter: (params) => resolveTipoMovimentacao(params.data).label,
      cellRenderer: (params) => {
        const { label, kind } = resolveTipoMovimentacao(params.data);

        return (
          <span
            className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold ${TIPO_BADGE_CLASS[kind]}`}
          >
            {label}
          </span>
        );
      },
    },
    {
      field: "descricao",
      headerName: "Descrição",
      filter: false,
      sortable: false,
      flex: 1,
      valueGetter: (params) => getMovementDescription(params.data),
    },
    {
      field: "valor_movimentacao",
      headerName: "Valor (R$)",
      filter: true,
      width: 170,
      type: "numberColumn",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value !== undefined && params.value !== null) {
          return formatCurrency(Number(params.value));
        }
        return "-";
      },
    },
  ];

  const fetchDashboard = useCallback(async () => {
    try {
      setDashboardLoading(true);
      const today = new Date();
      const initialDate = new Date();
      initialDate.setDate(today.getDate() - selectedDashboardPeriod.days);

      const response = await api.post("/listar/financeiroCaixaGeral", {
        qtd_por_pagina: dashboardPeriod === "year" ? 1500 : 800,
        order_by: "data_historico",
        order_direction: "desc",
        data_inicial: formatDateToApi(initialDate),
        data_final: formatDateToApi(today),
      });

      setDashboardRows(response.data?.data || []);
    } catch (error) {
      console.error("Erro ao carregar dashboard do caixa geral:", error);
      setDashboardRows([]);
    } finally {
      setDashboardLoading(false);
    }
  }, [dashboardPeriod, selectedDashboardPeriod.days]);

  const fetchResumo = useCallback(async () => {
    try {
      const response = await api.post("/listar/financeiroCaixaGeral/resumo", {});
      setResumo(response.data || null);
    } catch (error) {
      console.error("Erro ao carregar resumo do caixa geral:", error);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    fetchResumo();
  }, [fetchResumo]);

  // Cards do topo: vêm do backend e só contam movimentações posteriores à
  // última "edição valor no cofre" (marco de conciliação).
  const dashboardTotals = useMemo(() => ({
    totalNoCofre: Number(resumo?.total_no_cofre || 0),
    totalEntradas: Number(resumo?.total_entradas || 0),
    totalSaidas: Number(resumo?.total_saidas || 0),
    qtdEntradas: Number(resumo?.qtd_entradas || 0),
    qtdSaidas: Number(resumo?.qtd_saidas || 0),
  }), [resumo]);

  const chartData = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const initialDate = new Date();
    initialDate.setDate(today.getDate() - selectedDashboardPeriod.days);
    initialDate.setHours(0, 0, 0, 0);

    const stepDays = Math.ceil(selectedDashboardPeriod.days / selectedDashboardPeriod.points);

    const labels = Array.from({ length: selectedDashboardPeriod.points }).map((_, index) => {
      const start = new Date(initialDate);
      start.setDate(initialDate.getDate() + (index * stepDays));
      start.setHours(0, 0, 0, 0);

      const end = new Date(initialDate);
      end.setDate(initialDate.getDate() + ((index + 1) * stepDays) - 1);
      end.setHours(23, 59, 59, 999);

      if (end > today) {
        end.setTime(today.getTime());
      }

      return { start, end };
    });

    const rowsByPeriod = labels.map(({ start, end }) => {
      const rows = dashboardRows.filter((row) => {
        if (!row.data_historico) return false;
        const rowDate = new Date(row.data_historico.replace(" ", "T"));
        return rowDate >= start && rowDate <= end;
      });

      const label = dashboardPeriod === "year"
        ? start.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "")
        : end.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }).replace(".", "");

      return {
        label,
        entradas: rows
          .filter((row) => Number(row.valor_movimentacao || 0) >= 0)
          .reduce((total, row) => total + Number(row.valor_movimentacao || 0), 0),
        saidas: rows
          .filter((row) => Number(row.valor_movimentacao || 0) < 0)
          .reduce((total, row) => total + Math.abs(Number(row.valor_movimentacao || 0)), 0),
      };
    });

    const maxValue = Math.max(...rowsByPeriod.flatMap((item) => [item.entradas, item.saidas]), 1);

    return rowsByPeriod.map((item) => ({
      ...item,
      entradasPercent: Math.max((item.entradas / maxValue) * 100, item.entradas > 0 ? 4 : 0),
      saidasPercent: Math.max((item.saidas / maxValue) * 100, item.saidas > 0 ? 4 : 0),
    }));
  }, [dashboardPeriod, dashboardRows, selectedDashboardPeriod.days, selectedDashboardPeriod.points]);

  const operatorDistribution = useMemo(() => {
    const totalsByOperator = dashboardRows.reduce<Record<string, number>>((acc, row) => {
      const operator = row.usuario || "Outros";
      acc[operator] = (acc[operator] || 0) + 1;
      return acc;
    }, {});

    const total = Object.values(totalsByOperator).reduce((sum, value) => sum + value, 0) || 1;

    return Object.entries(totalsByOperator)
      .sort(([, a], [, b]) => b - a)
      .map(([operator, count], index) => ({
        operator,
        count,
        percent: Math.round((count / total) * 100),
        color: ["#062D4E", "#FFD85A", "#476586", "#E6E8EC", "#10B981", "#EF4444", "#8B5CF6", "#F97316"][index % 8],
      }));
  }, [dashboardRows]);

  const operatorChartBackground = useMemo(() => {
    if (operatorDistribution.length === 0) {
      return "#F3F4F6";
    }

    let currentPercent = 0;
    const slices = operatorDistribution.filter((item) => item.percent > 0).map((item) => {
      const start = currentPercent;
      const animatedPercent = item.percent * chartProgress;
      const end = Math.min(currentPercent + animatedPercent, 100);
      currentPercent = end;

      return `${item.color} ${start}% ${end}%`;
    });

    if (currentPercent < 100) {
      slices.push(`#F3F4F6 ${currentPercent}% 100%`);
    }

    return `conic-gradient(${slices.join(", ")})`;
  }, [chartProgress, operatorDistribution]);

  useEffect(() => {
    if (dashboardLoading) {
      setChartProgress(0);
      return;
    }

    setChartProgress(0);
    const duration = 700;
    const startTime = performance.now();
    let animationFrame = 0;

    const animate = (currentTime: number) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      setChartProgress(easedProgress);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [dashboardLoading, dashboardRows, dashboardPeriod]);

  useEffect(() => {
    if (!addMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target as Node)) {
        setAddMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [addMenuOpen]);

  const refreshCaixaData = useCallback(() => {
    gridRef.current?.refresh();
    void fetchDashboard();
    void fetchResumo();
  }, [fetchDashboard, fetchResumo]);

  const openCorrectionValueModal = () => {
    setCorrectedVaultValue(dashboardTotals.totalNoCofre);
    setIsCorrectionConfirmOpen(false);
    setIsCorrectionModalOpen(true);
  };

  const closeCorrectionValueModal = () => {
    setIsCorrectionModalOpen(false);
    setCorrectedVaultValue(undefined);
  };

  const handleCorrectVaultValue = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();

    if (!canEditCofre) {
      FrontendNotification("Você não tem permissão para editar o total no cofre.", "warning");
      return;
    }

    const newVaultValue = Number(correctedVaultValue || 0);

    if (newVaultValue < 0) {
      FrontendNotification("Informe um valor válido para o cofre.", "warning");
      return;
    }

    if (newVaultValue === dashboardTotals.totalNoCofre) {
      FrontendNotification("O valor informado é igual ao saldo atual do cofre.", "warning");
      return;
    }

    try {
      setCorrectionLoading(true);
      await api.post("/editar/financeiroCaixaGeral/valorCofre", {
        total_no_cofre: newVaultValue,
        id_usuario_historico: user?.id || 1,
      });

      FrontendNotification("Valor do cofre corrigido com sucesso!", "success");
      closeCorrectionValueModal();
      refreshCaixaData();
    } catch (error) {
      console.error("Erro ao corrigir valor do cofre:", error);
      FrontendNotification("Erro ao corrigir valor do cofre.", "error");
    } finally {
      setCorrectionLoading(false);
    }
  };

  const onDelete = useCallback(async (entityId?: number) => {
    try {
      if (!entityId) return;
      setLoading(true);
      await api.post("/deletar/financeiroCaixaGeral", {
        id_financeiro_caixa_geral: entityId,
      });
      setLoading(false);
      setIsRemove(false);
      refreshCaixaData();
    } catch {
      setLoading(false);
    }
  }, [refreshCaixaData]);

  const openCreateModal = (movementType: CaixaMovementType) => {
    if (movementType === "deposito" && !canDeposit) {
      FrontendNotification("Você não tem permissão para adicionar depósito.", "warning");
      return;
    }
    if (movementType === "retirada" && !canWithdraw) {
      FrontendNotification("Você não tem permissão para adicionar retirada.", "warning");
      return;
    }
    setSelectedMovementType(movementType);
    setSelectedRow(undefined);
    setIsEdit(false);
    setAddMenuOpen(false);
    openModal();
  };

  return (
    <>
      <Loading loading={loading} />

      {isRemove && (
        <ModalDelete
          onCancel={() => setIsRemove(!isRemove)}
          onConfirm={() => onDelete(selectedRow?.id_financeiro_caixa_geral)}
          row={`Registro #${selectedRow?.id_financeiro_caixa_geral}`}
        />
      )}

      {isCorrectionConfirmOpen && (
        <ModalDelete
          title="Corrigir valor no cofre?"
          message="Você deseja corrigir manualmente o valor atual no cofre?"
          onCancel={() => setIsCorrectionConfirmOpen(false)}
          onConfirm={openCorrectionValueModal}
        />
      )}

      {isCorrectionModalOpen && (
        <CustomModal title="Corrigir valor no cofre" onClose={closeCorrectionValueModal}>
          <form onSubmit={handleCorrectVaultValue} className="w-full p-4">
            <div className="mb-6">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Valor atual no cofre
              </label>
              <p className="mb-2 text-xs text-gray-500">
                Informe o valor correto que deve ficar registrado no cofre.
              </p>
              <NumericFormat
                value={correctedVaultValue ?? ""}
                onValueChange={(values) => setCorrectedVaultValue(values.floatValue)}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                prefix="R$ "
                placeholder="R$ 0,00"
                className="w-full rounded-full border border-[#F0A800] bg-white px-4 py-3 text-sm text-[#1E2121] outline-none transition focus:border-[#062D4E] focus:ring-2 focus:ring-[#062D4E]/10"
              />
            </div>

            <div className="flex justify-end border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={closeCorrectionValueModal}
                className="mr-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={correctionLoading}
                className="rounded-md bg-[#062D4E] px-4 py-2 text-sm font-medium text-white hover:bg-[#05233d] disabled:opacity-70"
              >
                {correctionLoading ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </form>
        </CustomModal>
      )}

      {isView && (
        <Info
          data={selectedRow}
          title="Conhecer - Caixa Geral"
          onClose={() => setIsView(false)}
        />
      )}

      {isModalOpen && (
        <Create
          isEdit={isEdit}
          selectedRow={selectedRow}
          movementType={selectedMovementType}
          onClear={() => {
            setIsEdit(false);
            closeModal();
          }}
          onConfirm={() => {
            setIsEdit(false);
            closeModal();
            refreshCaixaData();
          }}
        />
      )}

      <div className="flex h-[calc(100dvh-4rem)] min-h-0 w-full flex-col overflow-y-auto bg-[#F5F7FA] p-4 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between w-full mb-5">
          <div>
            <h1 className="text-2xl text-[#071B3A] font-bold">Caixa Geral</h1>
            <p className="text-sm text-[#6B7280] mt-1">Resumo das movimentações e saldo atual.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-[#DDE3EA] bg-white text-xs font-semibold text-[#1F2937] shadow-sm"
            >
              <CalendarDays size={15} />
              {selectedDashboardPeriod.label}
            </button>
            {canUseAddMenu && (
            <div className="relative" ref={addMenuRef}>
              <button
                type="button"
                className="h-10 px-4 inline-flex items-center justify-center gap-2 rounded-md bg-[#062D4E] text-[#FFFFFF] text-sm font-semibold border-none shadow-sm transition-colors hover:bg-[#0A3D66]"
                onClick={() => setAddMenuOpen((isOpen) => !isOpen)}
                id="btnCreate"
                aria-expanded={addMenuOpen}
              >
                <Plus size={16} />
                Adicionar
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${addMenuOpen ? "rotate-180" : ""}`}
                />
              </button>

              <div
                className={`absolute right-0 top-12 z-30 w-56 overflow-hidden rounded-lg border border-[#DDE3EA] bg-white shadow-xl transition-all duration-200 ease-out ${
                  addMenuOpen
                    ? "max-h-40 translate-y-0 opacity-100"
                    : "pointer-events-none max-h-0 -translate-y-2 opacity-0"
                }`}
              >
                {canDeposit && (
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-semibold text-[#17213D] transition-colors hover:bg-emerald-50"
                  onClick={() => openCreateModal("deposito")}
                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                    <ArrowDown size={16} />
                  </span>
                  Depósito
                </button>
                )}
                {canWithdraw && (
                <button
                  type="button"
                  className="flex w-full items-center gap-3 border-t border-[#EEF2F6] px-4 py-3 text-left text-sm font-semibold text-[#17213D] transition-colors hover:bg-red-50"
                  onClick={() => openCreateModal("retirada")}
                >
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-50 text-red-600">
                    <ArrowUp size={16} />
                  </span>
                  Retirada
                </button>
                )}
              </div>
            </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-lg bg-[#062D4E] p-5 text-white shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#FFD85A]">Total no cofre</span>
              <div className="flex items-center gap-2">
                {canEditCofre && (
                <button
                  type="button"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-gray-300 transition hover:bg-white/15 hover:text-white"
                  onClick={() => setIsCorrectionConfirmOpen(true)}
                  aria-label="Corrigir valor no cofre"
                  title="Corrigir valor no cofre"
                >
                  <Pencil size={16} />
                </button>
                )}
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-[#FFD85A]">
                  <Wallet size={18} />
                </span>
              </div>
            </div>
            <strong className="mt-8 block text-3xl font-bold leading-tight">
              <AnimatedNumber
                value={dashboardTotals.totalNoCofre}
                formatter={formatCurrencyCompact}
              />
            </strong>
            <span className="mt-2 block text-xs text-[#9FE6C4]">
              {dashboardLoading ? "Atualizando saldo..." : "Saldo após a última movimentação"}
            </span>
          </div>

          <div className="rounded-lg border border-[#E4EAF0] bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#4B5563]">Total de entradas</span>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
                <ArrowDown size={18} />
              </span>
            </div>
            <strong className="mt-12 block text-xl font-bold text-[#17213D]">
              <AnimatedNumber
                value={dashboardTotals.totalEntradas}
                formatter={formatCurrencyCompact}
              />
            </strong>
            <span className="mt-1 block text-xs text-[#374151]">
              <AnimatedNumber value={dashboardTotals.qtdEntradas} /> depósitos confirmados
            </span>
          </div>

          <div className="rounded-lg border border-[#E4EAF0] bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#4B5563]">Total de saídas</span>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-50 text-red-500">
                <ArrowUp size={18} />
              </span>
            </div>
            <strong className="mt-12 block text-xl font-bold text-[#17213D]">
              <AnimatedNumber
                value={dashboardTotals.totalSaidas}
                formatter={formatCurrencyCompact}
              />
            </strong>
            <span className="mt-1 block text-xs text-[#374151]">
              <AnimatedNumber value={dashboardTotals.qtdSaidas} /> retiradas processadas
            </span>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(280px,0.9fr)]">
          <section className="rounded-lg border border-[#E4EAF0] bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold text-[#17213D]">Fluxo de Caixa ({selectedDashboardPeriod.label})</h2>
                <div className="mt-2 flex items-center gap-4 text-xs text-[#4B5563]">
                  <span className="inline-flex items-center gap-2"><i className="h-2 w-2 rounded-full bg-emerald-400" />Entradas</span>
                  <span className="inline-flex items-center gap-2"><i className="h-2 w-2 rounded-full bg-rose-400" />Saídas</span>
                </div>
              </div>
              <div className="flex flex-wrap rounded-md border border-[#DDE3EA] bg-[#F8FAFC] p-1">
                {DASHBOARD_PERIODS.map((period) => (
                  <button
                    key={period.value}
                    type="button"
                    onClick={() => setDashboardPeriod(period.value)}
                    className={`h-8 rounded px-3 text-xs font-semibold transition-colors ${
                      dashboardPeriod === period.value
                        ? "bg-[#062D4E] text-white shadow-sm"
                        : "text-[#4B5563] hover:bg-white"
                    }`}
                  >
                    {period.shortLabel}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex h-64 items-end gap-4 border-t border-dashed border-[#E5E7EB] pt-6 sm:gap-8">
              {chartData.map((item) => (
                <div key={item.label} className="flex h-full flex-1 flex-col justify-end">
                  <div className="flex h-full items-end justify-center gap-1">
                    <div
                      className="w-full max-w-10 rounded-t-sm bg-emerald-200 transition-all duration-700 ease-out"
                      style={{ height: `${item.entradasPercent * chartProgress}%` }}
                      title={`Entradas: ${formatCurrency(item.entradas)}`}
                    />
                    <div
                      className="w-full max-w-10 rounded-t-sm bg-rose-300 transition-all duration-700 ease-out"
                      style={{ height: `${item.saidasPercent * chartProgress}%` }}
                      title={`Saídas: ${formatCurrency(item.saidas)}`}
                    />
                  </div>
                  <span className="mt-3 text-center text-[11px] text-[#6B7280]">{item.label}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-[#E4EAF0] bg-white p-5 shadow-sm">
            <h2 className="text-sm font-bold text-[#17213D]">Distribuição por Operador</h2>
            <div
              className="mx-auto my-6 flex h-32 w-40 items-center justify-center rounded-lg text-white shadow-inner transition-[background] duration-700"
              style={{ background: operatorChartBackground }}
            >
              <div className="rounded-lg bg-white px-7 py-6 text-center text-[#17213D] shadow-md">
                <span className="block text-[10px] font-bold uppercase text-[#6B7280]">Total</span>
                <strong className="text-lg">
                  <AnimatedNumber value={dashboardRows.length} />
                </strong>
                <span className="ml-1 text-xs font-semibold">Op.</span>
              </div>
            </div>
            <div className="max-h-36 space-y-3 overflow-y-auto overscroll-contain pr-2">
              {operatorDistribution.length > 0 ? operatorDistribution.map((item) => (
                <div key={item.operator} className="flex items-center justify-between gap-3 text-xs">
                  <span className="flex min-w-0 items-center gap-2 text-[#1F2937]">
                    <i className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: item.color }} />
                    <span className="truncate">{item.operator}</span>
                  </span>
                  <strong className="text-[#4B5563]">
                    <AnimatedNumber value={item.percent} formatter={(value) => `${Math.round(value)}%`} />
                  </strong>
                </div>
              )) : (
                <span className="block text-center text-sm text-[#6B7280]">Sem movimentações no período.</span>
              )}
            </div>
          </section>
        </div>

        <section className="mt-4 rounded-lg border border-[#E4EAF0] bg-white p-4 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-[#17213D]">Últimas Movimentações</h2>
              <p className="text-xs text-[#6B7280]">Depósitos e retiradas separados pelo tipo da movimentação.</p>
            </div>
          </div>
          <Grid
            ref={gridRef}
            columns={columns}
            filters={[]}
            pagination
            isModal
            path="/listar/financeiroCaixaGeral"
            onUpdate={(data: ICaixaGeral) => {
              setSelectedRow(data);
              setSelectedMovementType(Number(data.valor_movimentacao || 0) < 0 ? "retirada" : "deposito");
              setIsEdit(true);
              openModal();
            }}
            onDelete={(data: ICaixaGeral) => {
              setIsRemove(true);
              setSelectedRow(data);
            }}
            onView={(data: ICaixaGeral) => {
              setSelectedRow(data);
              setIsView(true);
            }}
            status={[]}
          />
        </section>
        </div>
    </>
  );
};

export default CaixaGeral;
